import { appBus } from "@src/features/events";
import { appStore } from "@src/stores/app";
import { PositiveInt } from "@src/utils/types";
import { useSelector } from "@xstate/react";
import { type Actor, assign, createActor, setup } from "xstate";

/**
 * Lifecycle status names exposed to consumers (preserved from the previous
 * Zustand-backed store so existing UI consumers don't have to change).
 */
export type EncounterStatus =
  | "idle"
  | "connected"
  | "in_progress"
  | "finalizing"
  | "completed";

export type EncounterContext = {
  encounterId: PositiveInt | null;
};

/**
 * Events accepted by the encounter machine.
 *
 * - `ENCOUNTER_ID_CHANGED` – fired from URL/route changes or imperative writers
 *   (`registerEncounterId`); id can be `null` to clear the active encounter.
 * - `TRANSCRIPTION_CREATED` – fired from the create-encounter flow on `/today`
 *   once the server has materialized an encounter row.
 * - `SNAPSHOT_RECEIVED` / `TRANSCRIPT_SNAPSHOT_RECEIVED` – SSE-driven status
 *   transitions (`encounter.snapshot` and `transcript.snapshot`).
 * - `FINALIZING` / `COMPLETED` / `REOPENED` – SSE-driven encounter lifecycle.
 * - `SUPERSEDED` – the active encounter was reopened as a new encounter row;
 *   carries the new id so it cannot accidentally be omitted.
 * - `PATIENT_CLEARED` – the patient context has been torn down; full reset.
 */
export type EncounterEvent =
  | { type: "ENCOUNTER_ID_CHANGED"; encounterId: PositiveInt | null }
  | { type: "TRANSCRIPTION_CREATED"; encounterId: PositiveInt }
  | { type: "SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_SNAPSHOT_RECEIVED" }
  | { type: "TRANSCRIPT_ITEM_CREATED" }
  | { type: "FINALIZING" }
  | { type: "COMPLETED" }
  | { type: "REOPENED" }
  | { type: "SUPERSEDED"; newEncounterId: PositiveInt }
  | { type: "PATIENT_CLEARED" };

/**
 * Statechart for the active encounter.
 *
 * The chart guarantees that illegal lifecycle transitions are dropped instead
 * of accepted (e.g. `FINALIZING` while `idle` is a no-op rather than poisoning
 * the status), and that {@link EncounterContext.encounterId} can only change
 * via well-typed events.
 *
 * `ENCOUNTER_ID_CHANGED` is hoisted to a top-level handler because the URL/
 * route parameters can change in any state and the previous Zustand
 * implementation always reset status to `idle` on id changes.
 */
export const encounterMachine = setup({
  types: {
    context: {} as EncounterContext,
    events: {} as EncounterEvent,
  },
  actions: {
    assignIdFromEvent: assign({
      encounterId: ({ event }) => {
        if (
          event.type === "ENCOUNTER_ID_CHANGED" ||
          event.type === "TRANSCRIPTION_CREATED"
        ) {
          return event.encounterId;
        }
        return null;
      },
    }),
    assignNewIdFromSupersededEvent: assign({
      encounterId: ({ event }) => {
        if (event.type === "SUPERSEDED") return event.newEncounterId;
        return null;
      },
    }),
    clearEncounterId: assign({ encounterId: () => null }),
  },
}).createMachine({
  id: "encounter",
  initial: "idle",
  context: { encounterId: null },
  on: {
    ENCOUNTER_ID_CHANGED: {
      target: ".idle",
      actions: { type: "assignIdFromEvent" },
    },
    TRANSCRIPTION_CREATED: {
      target: ".idle",
      actions: { type: "assignIdFromEvent" },
    },
    PATIENT_CLEARED: {
      target: ".idle",
      actions: { type: "clearEncounterId" },
    },
  },
  states: {
    idle: {
      on: {
        SNAPSHOT_RECEIVED: { target: "connected" },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    connected: {
      on: {
        SNAPSHOT_RECEIVED: { target: "connected" },
        TRANSCRIPT_SNAPSHOT_RECEIVED: { target: "in_progress" },
        TRANSCRIPT_ITEM_CREATED: { target: "in_progress" },
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    in_progress: {
      on: {
        FINALIZING: { target: "finalizing" },
        COMPLETED: { target: "completed" },
        SUPERSEDED: {
          target: "connected",
          actions: { type: "assignNewIdFromSupersededEvent" },
        },
      },
    },
    finalizing: {
      on: {
        COMPLETED: { target: "completed" },
        REOPENED: { target: "in_progress" },
      },
    },
    completed: {
      on: {
        REOPENED: { target: "in_progress" },
        SUPERSEDED: {
          target: "connected",
          actions: { type: "assignNewIdFromSupersededEvent" },
        },
      },
    },
  },
});

/**
 * Singleton actor that holds the active encounter id and lifecycle status.
 *
 * Started eagerly so external callers (`sendEncounterEvent`,
 * `matchesActiveEncounter`) can rely on it being live before any React
 * component subscribes. Bus → actor wiring lives in `RouteParamSync`
 * (URL → actor) and the SSE-bus subscribers below.
 */
export const encounterActor: Actor<typeof encounterMachine> =
  createActor(encounterMachine).start();

/**
 * Type-safe entrypoint for dispatching encounter events. Writers that aren't
 * driven by `appBus` (route effects, Storybook decorators, tests) call this
 * directly; the machine — not the caller — decides whether the event is
 * accepted in the current state.
 */
export function sendEncounterEvent(event: EncounterEvent): void {
  encounterActor.send(event);
}

/**
 * Whether `eventEncounterId` is the current encounter id in the actor
 * (synchronous read of the latest snapshot).
 */
export function matchesActiveEncounter(eventEncounterId: number): boolean {
  const active = encounterActor.getSnapshot().context.encounterId;
  return active != null && active === eventEncounterId;
}

/** Sets the active encounter id (e.g. `/today` after create-encounter). */
export function registerEncounterId(id: number | undefined): void {
  sendEncounterEvent({
    type: "ENCOUNTER_ID_CHANGED",
    encounterId: PositiveInt.parse(id),
  });
}

const onTranscriptionCreated = (payload: { encounterId: number }) => {
  const encounterId = PositiveInt.parse(payload.encounterId);
  if (encounterId == null) return;
  encounterActor.send({ type: "TRANSCRIPTION_CREATED", encounterId });
};

const onEncounterSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "SNAPSHOT_RECEIVED" });
};

const onEncounterCompleted = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "COMPLETED" });
};

const onEncounterFinalizing = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "FINALIZING" });
};

const onEncounterReopened = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "REOPENED" });
};

const onTranscriptSnapshot = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_SNAPSHOT_RECEIVED" });
};

const onTranscriptItemCreated = (payload: { encounterId: number }) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  encounterActor.send({ type: "TRANSCRIPT_ITEM_CREATED" });
};

const onEncounterSupersededByReopen = (payload: {
  encounterId: number;
  newEncounterId: number;
}) => {
  if (!matchesActiveEncounter(payload.encounterId)) return;
  const newEncounterId = PositiveInt.parse(payload.newEncounterId);
  if (newEncounterId == null) return;
  encounterActor.send({ type: "SUPERSEDED", newEncounterId });
};

const onParamsChanged = (payload: {
  params: Record<string, string | undefined>;
}) => {
  const patientId = PositiveInt.parse(payload.params.patientId);
  const encounterId = PositiveInt.parse(payload.params.encounterId);

  const currentPatientId = appStore.getState().patient_id;
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (patientId !== currentPatientId) {
    appStore.getState().setPatientId(patientId);
    encounterActor.send({ type: "PATIENT_CLEARED" });
  }

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

const onSearchParamsChanged = (payload: { searchParams: URLSearchParams }) => {
  const encounterId = PositiveInt.parse(
    payload.searchParams.get("encounterId"),
  );
  const currentEncounterId = encounterActor.getSnapshot().context.encounterId;

  if (encounterId !== currentEncounterId) {
    encounterActor.send({
      type: "ENCOUNTER_ID_CHANGED",
      encounterId: encounterId,
    });
  }
};

appBus.on("encounter.transcription_created", onTranscriptionCreated);
appBus.on("encounter.snapshot", onEncounterSnapshot);
appBus.on("encounter.completed", onEncounterCompleted);
appBus.on("encounter.finalizing", onEncounterFinalizing);
appBus.on("encounter.reopened", onEncounterReopened);
appBus.on("transcript.snapshot", onTranscriptSnapshot);
appBus.on("transcript.item.created", onTranscriptItemCreated);
appBus.on("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
appBus.on("client.router_params_changed", onParamsChanged);
appBus.on("client.router_search_params_changed", onSearchParamsChanged);

export function useEncounterId(): PositiveInt | null {
  return useSelector(encounterActor, (s) => s.context.encounterId);
}

export function useEncounterStatusValue(): EncounterStatus {
  return useSelector(encounterActor, (s) => s.value as EncounterStatus);
}

export function useIsEncounterCompleted(): boolean {
  return useSelector(encounterActor, (s) => s.matches("completed"));
}

export function useIsEncounterFinalizing(): boolean {
  return useSelector(encounterActor, (s) => s.matches("finalizing"));
}

export function useIsEncounterConnected(): boolean {
  return useSelector(encounterActor, (s) => !s.matches("idle"));
}

/**
 * Tear down the SSE bus subscriptions and stop the actor. Intended for test
 * environments that need a fully-isolated fixture; production code should
 * never call this.
 */
export function __resetForTesting(): void {
  appBus.off("encounter.transcription_created", onTranscriptionCreated);
  appBus.off("encounter.snapshot", onEncounterSnapshot);
  appBus.off("encounter.completed", onEncounterCompleted);
  appBus.off("encounter.finalizing", onEncounterFinalizing);
  appBus.off("encounter.reopened", onEncounterReopened);
  appBus.off("transcript.snapshot", onTranscriptSnapshot);
  appBus.off("transcript.item.created", onTranscriptItemCreated);
  appBus.off("encounter.superseded_by_reopen", onEncounterSupersededByReopen);
  appBus.off("client.router_params_changed", onParamsChanged);
  appBus.off("client.router_search_params_changed", onSearchParamsChanged);

  encounterActor.stop();
}
